/**
 * Where each aircraft's picture comes from.
 *
 * The server stores and classifies stream locations; it does not carry video.
 * The browser connects to the stream itself, which keeps a small box out of
 * the path of a fleet's H.264 — and means the URL has to reach the client.
 */

import { api, authHeaders, resolveUrl, type ApiResult } from '@/api/client';
import type { CommandResult } from '@/types/domain';

export type Playable = 'native' | 'needs-gateway' | 'no';

export interface GatewayCapabilities {
  enabled: boolean;
  transcode?: boolean;
  mjpeg?: boolean;
}

export interface VideoSource {
  droneId: string;
  label: string;
  /** Redacted in listings, real when fetched for one aircraft. */
  url: string;
  kind: string;
  playable: Playable;
  note: string;
  hasCredentials: boolean;
  /** True when this deployment's gateway restreams the feed to the browser. */
  viaGateway?: boolean;
  gateway?: GatewayCapabilities;
}

export type PlaybackVariant = 'direct' | 'h264' | 'mjpeg';

/**
 * Where to connect for one viewing, and the credential to do it with. The
 * ticket is good for one connection for about thirty seconds; it goes in an
 * Authorization header, never into the URL.
 */
export interface Playback {
  droneId: string;
  variant: PlaybackVariant;
  url: string;
  ticket: string;
  expiresInS: number;
  transcode: boolean;
}

export function requestPlayback(
  droneId: string,
  variant: PlaybackVariant,
): Promise<ApiResult<Playback>> {
  // Unlogged: a reconnect loop on a flaky link would otherwise fill the
  // activity log with ticket requests nobody asked to see.
  return api.post<Playback>(
    `/video/${encodeURIComponent(droneId)}/play`,
    { variant },
    { logged: false },
  );
}

export interface Recording {
  id: number;
  droneId: string;
  startedAt: number;
  startedBy: string;
  stoppedAt: number | null;
  stoppedBy: string | null;
  stopReason: string | null;
  sessionId: string | null;
  durationS: number;
  sizeBytes: number;
  files: number;
  codec: string | null;
  active: boolean;
}

export interface RecordingList {
  droneId: string;
  recordings: Recording[];
  recording: Recording | null;
  enabled: boolean;
  disk: {
    freeBytes: number;
    usedBytes: number;
    maxTotalBytes: number;
    minFreeBytes: number;
    maxDurationS: number;
  };
}

export function listRecordings(droneId: string): Promise<ApiResult<RecordingList>> {
  return api.get<RecordingList>(`/video/${encodeURIComponent(droneId)}/recordings`, {
    logged: false,
  });
}

export function startRecording(droneId: string): Promise<ApiResult<Recording>> {
  return api.post<Recording>(`/video/${encodeURIComponent(droneId)}/recording`, undefined, {
    label: `RECORD ${droneId}`,
  });
}

export function stopRecording(droneId: string): Promise<ApiResult<Recording>> {
  return api.delete<Recording>(`/video/${encodeURIComponent(droneId)}/recording`, {
    label: `STOP RECORDING ${droneId}`,
  });
}

/**
 * Save one file of a recording. Fetched with the session's own Authorization
 * header and handed to the browser as a download, because a plain link cannot
 * carry the header and a token in the link is what this codebase refuses.
 */
export async function downloadRecording(
  droneId: string,
  recordingId: number,
  index: number,
): Promise<string | null> {
  const path = `/video/${encodeURIComponent(droneId)}/recordings/${recordingId}/files/${index}`;
  let response: Response;
  try {
    response = await fetch(resolveUrl(path), { headers: authHeaders({ Accept: 'video/mp4' }) });
  } catch {
    return 'The server could not be reached.';
  }
  if (!response.ok) {
    return response.status === 410
      ? 'That clip is no longer on the server.'
      : `The server refused the download (${response.status}).`;
  }
  const blob = await response.blob();
  const name = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1];
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = name ?? `recording-${recordingId}.mp4`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
  return null;
}

/** A camera the connector found on the network next to the aircraft. */
export interface LanCamera {
  host: string;
  port: number;
  path: string;
  kind: 'rtsp' | 'mjpeg' | 'video' | 'snapshot';
  name: string;
  url: string;
  viaGateway: boolean;
}

export function listLanCameras(): Promise<
  ApiResult<{ cameras: LanCamera[]; reportedAt: number | null }>
> {
  return api.get<{ cameras: LanCamera[]; reportedAt: number | null }>('/video/lan/cameras', {
    logged: false,
  });
}

export function listVideoSources(): Promise<ApiResult<{ sources: VideoSource[] }>> {
  return api.get<{ sources: VideoSource[] }>('/video', { logged: false });
}

export function getVideoSource(droneId: string): Promise<ApiResult<VideoSource>> {
  return api.get<VideoSource>(`/video/${encodeURIComponent(droneId)}`, { logged: false });
}

export function saveVideoSource(
  droneId: string,
  body: { label: string; url: string },
): Promise<ApiResult<VideoSource>> {
  return api.put<VideoSource>(`/video/${encodeURIComponent(droneId)}`, body, {
    label: `SET VIDEO ${droneId}`,
  });
}

export function deleteVideoSource(droneId: string): Promise<ApiResult<{ removed: boolean }>> {
  return api.delete<{ removed: boolean }>(`/video/${encodeURIComponent(droneId)}`, {
    label: `CLEAR VIDEO ${droneId}`,
  });
}

/** One stream an aircraft's camera described in VIDEO_STREAM_INFORMATION. */
export interface DiscoveredStream {
  cameraComponent: number;
  streamId: number;
  name: string;
  /** A feed label the server will accept, made from the camera's own name. */
  label: string;
  /** The real address, credentials included, so it can be saved as the feed. */
  url: string;
  redactedUrl: string;
  hasCredentials: boolean;
  streamType: string;
  kind: string;
  playable: Playable;
  note: string;
  resolution: [number, number];
  framerate: number;
}

export interface StreamDiscovery {
  droneId: string;
  result: CommandResult;
  streams: DiscoveredStream[];
}

/**
 * Ask the aircraft's cameras where their video lives. Saves nothing: the
 * answer only prefills the feed form. The server waits up to `timeoutS` for a
 * camera to reply, so the request timeout leaves room for that wait.
 */
export function discoverVideoStreams(
  droneId: string,
  timeoutS = 5,
): Promise<ApiResult<StreamDiscovery>> {
  return api.post<StreamDiscovery>(`/video/${encodeURIComponent(droneId)}/discover`, undefined, {
    label: `FIND VIDEO ${droneId}`,
    query: { timeout_s: timeoutS },
    timeoutMs: (timeoutS + 10) * 1000,
  });
}
